// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  IlliquidAssetEventType,
  type IlliquidAssetEvent,
} from '@shared/data-access/types/engine-types';
import { isDefined, isNullOrUndefined } from '@shared/utils';
import type { UTCTimestamp } from 'lightweight-charts';

/** Minimum valuation points required to render the value-evolution chart. */
export const MIN_ILLIQUID_VALUATION_CHART_POINTS = 2;

export type IlliquidValuationChartPoint = {
  /** Unique ascending chart time derived from `created_at` (unix seconds). */
  readonly time: UTCTimestamp;

  readonly value: number;

  /** Business date (`YYYY-MM-DD`) shown in the tooltip. */
  readonly eventDate: string;

  readonly currency: string;
};

const toUnixSeconds = (isoTimestamp: string): number | null => {
  const ms = Date.parse(isoTimestamp);

  if (Number.isNaN(ms)) return null;

  return Math.floor(ms / 1000);
};

/**
 * Builds area-series points from valuation events only.
 *
 * @remarks
 * Chart libraries require unique ascending times. Points are sorted and keyed by
 * {@link IlliquidAssetEvent.created_at} (not `event_date`) so same-day valuations do
 * not collide. Identical `created_at` values are nudged forward by 1s.
 */
export const buildIlliquidValuationChartPoints = (
  events: readonly IlliquidAssetEvent[] | undefined,
  fallbackCurrency: string,
): IlliquidValuationChartPoint[] => {
  if (isNullOrUndefined(events) || events.length === 0) return [];

  const valuations = events
    .filter(
      (event): event is IlliquidAssetEvent & { amount: number } =>
        event.event_type === IlliquidAssetEventType.Valuation &&
        isDefined(event.amount) &&
        typeof event.amount === 'number' &&
        Number.isFinite(event.amount),
    )
    .slice()
    .sort((a, b) => {
      const aTime = toUnixSeconds(a.created_at) ?? 0;

      const bTime = toUnixSeconds(b.created_at) ?? 0;

      return aTime - bTime;
    });

  let lastTime = Number.NEGATIVE_INFINITY;

  const points: IlliquidValuationChartPoint[] = [];

  for (const event of valuations) {
    const parsed = toUnixSeconds(event.created_at);

    if (isNullOrUndefined(parsed)) continue;

    let time = parsed;

    if (time <= lastTime) {
      time = lastTime + 1;
    }

    lastTime = time;

    points.push({
      time: time as UTCTimestamp,
      value: event.amount,
      eventDate: event.event_date,
      currency: event.currency ?? fallbackCurrency,
    });
  }

  return points;
};

export const canShowIlliquidValuationChart = (
  events: readonly IlliquidAssetEvent[] | undefined,
  fallbackCurrency: string,
): boolean =>
  buildIlliquidValuationChartPoints(events, fallbackCurrency).length >=
  MIN_ILLIQUID_VALUATION_CHART_POINTS;
